import { createFileRoute, Link, Outlet, useRouterState } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

/**
 * Settings, as one screen split into tabs rather than one long scroll.
 *
 * The tab bar lives on the layout, not on either page, because Integrations is
 * a route of its own — it has to be, since the webhook auto-disable email and
 * the Zoom reconnect email both link to it by URL, and it carries its own
 * second row of tabs. Putting the bar here is what keeps it feeling like a tab
 * of Settings rather than somewhere else the app sent you.
 */
export const Route = createFileRoute('/app/settings')({
  component: SettingsLayout,
});

/**
 * Where each tab lives.
 *
 * Three are sections of the index page, selected by a search param so a tab can
 * be linked to and survives a reload. The fourth is a real route. The bar does
 * not care which is which, and neither should anyone reading it.
 */
const TABS = [
  { key: 'profile', to: '/app/settings', search: { tab: 'profile' } },
  { key: 'notifications', to: '/app/settings', search: { tab: 'notifications' } },
  { key: 'products', to: '/app/settings', search: { tab: 'products' } },
  { key: 'integrations', to: '/app/settings/integrations' },
  { key: 'members', to: '/app/settings/members' },
  { key: 'activity', to: '/app/settings/activity' },
  /*
    Last, dimmer than the rest, and pushed to the far end where there is room
    for it. Deleting the account is not a setting anyone is looking for, and a
    tab that looks like the others invites the stray click. It is still plainly
    there — hiding it behind a confirmation maze would be worse — just not
    competing with the things people came to change.
  */
  { key: 'delete', to: '/app/settings', search: { tab: 'delete' }, quiet: true },
] as const;

export type SettingsTab = (typeof TABS)[number]['key'];

function SettingsLayout() {
  const { t } = useTranslation(['account']);
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const search = useRouterState({ select: (s) => s.location.search as { tab?: string } });
  // Two real routes now, so the check is "which route" rather than a boolean.
  const route = pathname.startsWith('/app/settings/integrations')
    ? 'integrations'
    : pathname.startsWith('/app/settings/members')
      ? 'members'
      : pathname.startsWith('/app/settings/activity')
        ? 'activity'
        : null;
  // Anything unrecognised lands on Profile, which is also the bare /app/settings.
  const current = route ?? search.tab ?? 'profile';

  return (
    <div className="max-w-2xl space-y-6">
      <h1 className="text-xl font-semibold">{t('account:title')}</h1>

      <nav className="-mx-4 flex gap-4 overflow-x-auto border-b border-neutral-200 px-4 text-sm [scrollbar-width:none] sm:mx-0 sm:px-0 [&::-webkit-scrollbar]:hidden">
        {TABS.map((tab) => {
          const active = current === tab.key;
          const quiet = 'quiet' in tab;
          return (
            <Link
              key={tab.key}
              to={tab.to}
              // Integrations is a route of its own and carries no search, so
              // the two shapes are written out: a conditional spread of an
              // optional prop is what exactOptionalPropertyTypes exists to
              // refuse.
              search={'search' in tab ? tab.search : {}}
              // ms-auto only once the row has spare width; on a narrow screen
              // the bar scrolls instead and an auto margin there would strand
              // the tab past the edge.
              className={`-mb-px flex min-h-11 shrink-0 items-center border-b-2 ${
                quiet ? 'sm:ms-auto ' : ''
              }${
                active
                  ? quiet
                    ? 'border-red-400 font-medium text-red-700'
                    : 'border-neutral-900 font-medium text-neutral-900'
                  : quiet
                    ? 'border-transparent text-neutral-400 hover:text-neutral-600'
                    : 'border-transparent text-neutral-500 hover:text-neutral-800'
              }`}
            >
              {t(`account:tabs.${tab.key}`)}
            </Link>
          );
        })}
      </nav>

      <Outlet />
    </div>
  );
}
